import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card } from "reactstrap";
import { Input, Select, Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import { Button, Icon } from "../../../components/Component";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import {
  AddAccess,
  EditAccess,
} from "../../../components/accessControl/AccessControl";
import { assetApis } from "../../../api/Assets";
import AssetEditor from "./AssetEditor";
import AssetStatusModal, { statusTagColor, statusLabel } from "./AssetStatusModal";

/**
 * Assets → Asset Master. Machinery / tools / equipment as assets, with a
 * generated code, a status lifecycle (Available / Assigned / Under Maintenance
 * / Retired) and maintenance expiry tracking.
 *
 * Drill-in pattern: the list swaps itself out for the editor (house
 * convention). Status changes go through AssetStatusModal — the one write path
 * for the lifecycle, so assignment fields can never drift from the status.
 */

/** Amber inside 30 days, red when past — the list's early-warning system. */
const expiryCell = (value: any) => {
  if (!value) return <span className="text-soft">—</span>;
  const date = dayjs(value);
  if (!date.isValid()) return <span className="text-soft">—</span>;
  const days = date.diff(dayjs(), "day");
  const color = days < 0 ? "#e85347" : days <= 30 ? "#f4bd0e" : "#8094ae";
  const label =
    days < 0 ? `${-days}d overdue` : days === 0 ? "today" : `in ${days}d`;
  return (
    <Tooltip title={date.format("DD MMM YYYY HH:mm")}>
      <span style={{ whiteSpace: "nowrap" }}>
        <span
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: 4,
            background: color,
            marginRight: 6,
          }}
        />
        {date.format("DD/MM/YY")}
        <span className="text-soft" style={{ marginLeft: 4, fontSize: 11 }}>
          {label}
        </span>
      </span>
    </Tooltip>
  );
};

const STATUS_FILTERS = [
  { value: "", label: "All statuses" },
  { value: "Available", label: "Available" },
  { value: "Assigned", label: "Assigned" },
  { value: "UnderMaintenance", label: "Under Maintenance" },
  { value: "Retired", label: "Retired" },
];

const AssetMasterList = () => {
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50"],
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [editorOpen, setEditorOpen] = useState<{ status: boolean; id: number | null }>({
    status: false,
    id: null,
  });
  const [statusModal, setStatusModal] = useState<{ open: boolean; asset: any | null }>({
    open: false,
    asset: null,
  });

  const fetchData = useCallback(
    async (params: any = {}) => {
      setLoading(true);
      try {
        const current = params.current ?? 1;
        const size = params.pageSize ?? pagination.pageSize;
        const res = await assetApis.getList({
          page: current,
          pageSize: size,
          // direction "P" means PLAIN OFFSET — the backend applies no keyset
          // condition for it, so paging is driven entirely by pageDiff.
          direction: "P",
          sortOrder: "DESC",
          // pageDiff is the ZERO-BASED PAGE INDEX, not a page number: the
          // backend computes skip = pageDiff x pageSize (fetchPaginatedList in
          // src/utils/pagination.utils.ts) and ignores `page` entirely.
          // Hardcoding 0 here made every page re-fetch the first one.
          pageDiff: Math.max(0, current - 1),
          paginationId: null,
          // explicit overrides win over state — same stale-closure guard the
          // norms lists use
          searchQuery: params.searchQuery !== undefined ? params.searchQuery : searchQuery || null,
          status:
            (params.status !== undefined ? params.status : statusFilter) || undefined,
        });
        if (res?.status) {
          const { list, count } = res.data || {};
          setDataSource(list || []);
          setPagination((prev) => ({
            ...prev,
            current,
            // Persist the size too, or the pager keeps rendering the old one
            // while the server is already returning the new page length.
            pageSize: size,
            total: Number(count) || 0,
          }));
        }
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pagination.pageSize, statusFilter, searchQuery],
  );

  useEffect(() => {
    fetchData({ current: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => fetchData({ current: pagination.current });

  const columns: any[] = useMemo(
    () => [
      {
        title: "Code",
        dataIndex: "assetCode",
        key: "assetCode",
        fixed: "left",
        width: 110,
        render: (code: string, record: any) => (
          <a
            href="#edit"
            onClick={(e) => {
              e.preventDefault();
              setEditorOpen({ status: true, id: record.assetId });
            }}
            style={{ fontWeight: 600, color: "#2557d6", whiteSpace: "nowrap" }}
          >
            {code}
          </a>
        ),
      },
      { title: "Name", dataIndex: "assetName", key: "assetName", width: 220 },
      {
        title: "Type",
        dataIndex: "toolTypeName",
        key: "toolTypeName",
        width: 150,
        render: (v: string) => v || <span className="text-soft">—</span>,
      },
      {
        title: "Category",
        key: "category",
        width: 160,
        render: (_: any, r: any) =>
          [r.category, r.subCategory].filter(Boolean).join(" / ") || (
            <span className="text-soft">—</span>
          ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 140,
        render: (status: string) => (
          <Tag color={statusTagColor(status)}>{statusLabel(status)}</Tag>
        ),
      },
      {
        title: "Project / Location",
        key: "where",
        width: 200,
        render: (_: any, r: any) =>
          [r.projectTitle, r.location].filter(Boolean).join(" · ") || (
            <span className="text-soft">—</span>
          ),
      },
      {
        title: "Assigned To",
        dataIndex: "assignedToName",
        key: "assignedToName",
        width: 140,
        render: (v: string) => v || <span className="text-soft">—</span>,
      },
      {
        title: "Serial No",
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: 140,
        render: (v: string) => v || <span className="text-soft">—</span>,
      },
      {
        title: "Maintenance Due",
        dataIndex: "maintenanceDueDate",
        key: "maintenanceDueDate",
        width: 160,
        render: expiryCell,
      },
      {
        title: "Warranty",
        dataIndex: "warrantyExpiryDate",
        key: "warrantyExpiryDate",
        width: 150,
        render: expiryCell,
      },
      {
        title: "Insurance",
        dataIndex: "insuranceExpiryDate",
        key: "insuranceExpiryDate",
        width: 150,
        render: expiryCell,
      },
      {
        title: "Action",
        key: "action",
        fixed: "right",
        width: 130,
        render: (_: any, record: any) => (
          <EditAccess moduleName="Norms">
            <Button
              size="sm"
              color="light"
              outline
              className="btn-white"
              onClick={() => setStatusModal({ open: true, asset: record })}
            >
              <span>Change Status</span>
            </Button>
          </EditAccess>
        ),
      },
    ],
    [],
  );

  if (editorOpen.status) {
    return (
      <>
        <Head title="Assets | Asset Master" />
        <Content>
          <Card className="card-bordered">
            <AssetEditor
              assetId={editorOpen.id}
              onClose={() => setEditorOpen({ status: false, id: null })}
              onSaved={() => {
                setEditorOpen({ status: false, id: null });
                refresh();
              }}
            />
          </Card>
        </Content>
      </>
    );
  }

  return (
    <>
      <Head title="Assets | Asset Master" />
      <Content>
        <Card className="card-bordered">
          <div className="card-inner card-inner-lg p-0">
            <div className="p-3 pb-0">
              <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap" style={{ gap: 8 }}>
                <h5 className="mb-0" style={{ color: "#022c57", fontWeight: 600 }}>
                  Asset Master
                </h5>
                <ul className="nk-ibx-head-tools g-1 m-0 p-0 d-flex align-items-center" style={{ gap: 8 }}>
                  <li className="pt-0 pb-0" style={{ minWidth: 220 }}>
                    <Input.Search
                      allowClear
                      placeholder="Code, name, serial, make…"
                      onSearch={(value) => {
                        setSearchQuery(value);
                        fetchData({ current: 1, searchQuery: value || null });
                      }}
                    />
                  </li>
                  <li className="pt-0 pb-0">
                    <Select
                      value={statusFilter}
                      onChange={(value) => {
                        setStatusFilter(value);
                        fetchData({ current: 1, status: value });
                      }}
                      options={STATUS_FILTERS}
                      style={{ width: 170 }}
                    />
                  </li>
                  <li className="pt-0 pb-0">
                    <AddAccess moduleName="Norms">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => setEditorOpen({ status: true, id: null })}
                      >
                        <Icon name="plus" />
                        <span>Add Asset</span>
                      </Button>
                    </AddAccess>
                  </li>
                </ul>
              </div>
            </div>

            <div className="content clearfix">
              <div style={{ width: "100%", margin: "10px 0", padding: "0 8px" }}>
                <SkeletonTable
                  loading={loading}
                  columns={columns as SkeletonTableColumnsType[]}
                >
                  <Table
                    rowKey="assetId"
                    columns={columns}
                    dataSource={dataSource}
                    pagination={pagination}
                    onChange={(page: any) =>
                      // A page-size change restarts at page 1: keeping the old
                      // page number can put the offset past the end of a now-
                      // shorter list, which returns an empty table.
                      page.pageSize !== pagination.pageSize
                        ? fetchData({ current: 1, pageSize: page.pageSize })
                        : fetchData({ current: page.current, pageSize: page.pageSize })
                    }
                    bordered
                    size="middle"
                    scroll={{ x: "max-content" }}
                  />
                </SkeletonTable>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      <AssetStatusModal
        open={statusModal.open}
        asset={statusModal.asset}
        onClose={() => setStatusModal({ open: false, asset: null })}
        onDone={refresh}
      />
    </>
  );
};

export default AssetMasterList;
